import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../../../../../utils/hooks/useMutationWithError";
import { updateDurationBookingPayloadAtom } from "../../../atoms/selectors/UpdatePayloads/updateDurationBookingPayload/updateDurationBookingPayloadAtom";
import { currentSessionMemberShipTypeIdAtom } from "../../../atoms/stores";
import type { PatronEditBooking } from "../../../types";
import { RecurringRequestPreviewConfirmation } from "../useSaveChangesToSingleSlotBooking/useSaveChangesToSingleSlotBooking";
import { useSaveChangesToMultiSlotBookingMutation } from "./__generated__/useSaveChangesToMultiSlotBookingMutation.graphql";

export const useSaveChangesToMultiSlotBooking = (
  successCallback: (options?: PatronEditBooking.SaveSuccessOptions) => void
) => {
  const { showToast } = useToast();
  const updateDurationBookingPayload = useAtomValue(
    updateDurationBookingPayloadAtom
  );
  const { patronLoyalty } = useFlags();

  const [saveChangesToMultiSlotBooking, isSavingChanges] =
    useMutationWithError<useSaveChangesToMultiSlotBookingMutation>(
      useSaveChangesToMultiSlotBookingString
    );
  const membershipTypeId = useAtomValue(currentSessionMemberShipTypeIdAtom);
  const handleSaveMultiSlotBooking = useCallback(
    (
      editScope: "THIS_ONLY" | "THIS_AND_FOLLOWING" = "THIS_ONLY",
      previewConfirmation?: RecurringRequestPreviewConfirmation
    ) => {
      if (!updateDurationBookingPayload) return;
      saveChangesToMultiSlotBooking({
        variables: {
          input: {
            ...updateDurationBookingPayload,
            editScope,
            ...(previewConfirmation ?? {}),
          },
          membershipTypeId: membershipTypeId ?? "",
          bookingType: "STANDARD",
          patronLoyalty: !!patronLoyalty,
        },
        onCompleted: () => {
          successCallback();
        },
        onCompletedErrorCustom: (errors) => {
          const committedWarning = errors.find(
            (error) =>
              error.code === "recurring_field_edit_post_commit_incomplete"
          );

          if (!committedWarning) return errors;

          successCallback({ hasPostCommitWarning: true });
          showToast({
            title: t`Changes saved`,
            description:
              committedWarning.message ??
              t`Your changes were saved, but some follow-up updates are still pending.`,
            semanticStyle: "warning",
          });
          return errors.filter((error) => error !== committedWarning);
        },
      });
    },
    [
      saveChangesToMultiSlotBooking,
      successCallback,
      updateDurationBookingPayload,
      membershipTypeId,
      patronLoyalty,
      showToast,
    ]
  );

  return [handleSaveMultiSlotBooking, isSavingChanges] as const;
};
const useSaveChangesToMultiSlotBookingString = graphql`
  mutation useSaveChangesToMultiSlotBookingMutation(
    $input: UpdateMultiSlotBookingInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: updateMultiSlotBooking(input: $input) {
      errors {
        code

        message
        field
      }
      success
      booking {
        ...BookingSummary_booking @arguments(patronLoyalty: $patronLoyalty)
      }
    }
  }
`;
